import React, { useEffect, useState } from 'react';
import { Mail, Send, RefreshCw, AlertCircle, CheckCircle, Clock, AtSign } from 'lucide-react';
import { saListOutbox, sendEmailNotification, sendMentionDigest } from '../../lib/db';
import { formatDate } from '../../lib/formatters';

const STATUS_FILTERS = ['all', 'pending', 'sent', 'failed'];

const STATUS_META = {
  pending: { Icon: Clock,        tone: 'bg-amber-900/40 text-amber-300' },
  sent:    { Icon: CheckCircle,  tone: 'bg-emerald-900/40 text-emerald-300' },
  failed:  { Icon: AlertCircle,  tone: 'bg-red-900/40 text-red-300' },
};

export default function SuperAdminNotificationsPage() {
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [filter,  setFilter]  = useState('all');
  const [working, setWorking] = useState(false);
  const [retrying, setRetrying] = useState(null);
  const [flashMsg, setFlashMsg] = useState('');
  const [digestBusy, setDigestBusy] = useState(false);

  const refresh = () => {
    setLoading(true);
    saListOutbox(filter === 'all' ? null : filter, 200).then(({ data, error: err }) => {
      if (err) setError(err.message || 'Could not load outbox');
      else setRows(data || []);
      setLoading(false);
    });
  };

  useEffect(refresh, [filter]);  // eslint-disable-line react-hooks/exhaustive-deps

  const flushPending = async () => {
    setWorking(true);
    setFlashMsg('');
    const { data, error: err } = await sendEmailNotification();
    setWorking(false);
    if (err?.message)       { setFlashMsg(`Send failed: ${err.message}`); refresh(); return; }
    if (data?.error)        { setFlashMsg(`Send failed: ${data.error}`);  refresh(); return; }
    setFlashMsg(`Processed ${data?.processed ?? 0} — sent ${data?.sent ?? 0}, failed ${data?.failed ?? 0}.`);
    refresh();
  };

  const buildDigests = async () => {
    setDigestBusy(true);
    setFlashMsg('');
    const { data, error: err } = await sendMentionDigest();
    setDigestBusy(false);
    if (err?.message) { setFlashMsg(`Digest build failed: ${err.message}`);  return; }
    if (data?.error)  { setFlashMsg(`Digest build failed: ${data.error}`);   return; }
    setFlashMsg(
      `Enqueued ${data?.enqueued ?? 0} digest${(data?.enqueued ?? 0) === 1 ? '' : 's'}`
      + ` covering ${data?.items ?? 0} unread mention${(data?.items ?? 0) === 1 ? '' : 's'}`
      + ` across ${data?.users ?? 0} user${(data?.users ?? 0) === 1 ? '' : 's'}.`
      + ' Click "Send pending" to dispatch them.'
    );
    refresh();
  };

  const retry = async (row) => {
    setRetrying(row.id);
    const { data, error: err } = await sendEmailNotification(row.id);
    setRetrying(null);
    if (err?.message) { alert(err.message); return; }
    if (data?.error)  { alert(data.error);  return; }
    refresh();
  };

  if (loading && rows.length === 0) return <div className="text-center py-20 text-slate-500">Loading…</div>;

  const counts = STATUS_FILTERS.reduce((acc, k) => {
    acc[k] = k === 'all' ? rows.length : rows.filter((r) => r.status === k).length;
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-white">Email notifications</h1>
          <p className="text-slate-400 text-sm mt-0.5">
            Outbound emails queued for delivery — plan-change confirmations and other system notices.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={refresh}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium transition disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button
            onClick={buildDigests}
            disabled={digestBusy}
            title="Scan unread @-mentions across all firms and enqueue a digest email for each user."
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium transition disabled:opacity-50"
          >
            <AtSign size={14} /> {digestBusy ? 'Building…' : 'Build mention digests'}
          </button>
          <button
            onClick={flushPending}
            disabled={working}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium transition disabled:opacity-50"
          >
            <Send size={14} /> {working ? 'Sending…' : 'Send pending'}
          </button>
        </div>
      </div>

      {error && (
        <div className="px-4 py-3 rounded-xl bg-red-900/30 border border-red-800 text-red-300 text-sm">
          {error}
        </div>
      )}
      {flashMsg && (
        <div className="px-4 py-3 rounded-xl bg-slate-800 border border-slate-700 text-slate-200 text-sm">
          {flashMsg}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 text-xs">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-2.5 py-1 rounded-lg capitalize transition ${
              filter === s
                ? 'bg-indigo-600 text-white'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
          >
            {s} {counts[s] !== undefined && <span className="opacity-70">({counts[s]})</span>}
          </button>
        ))}
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-slate-500 bg-slate-800/40">
            <tr>
              <th className="text-left px-5 py-3 font-medium">When</th>
              <th className="text-left px-5 py-3 font-medium">Recipient</th>
              <th className="text-left px-5 py-3 font-medium">Subject</th>
              <th className="text-left px-5 py-3 font-medium">Status</th>
              <th className="text-right px-5 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {rows.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-12 text-center text-slate-500">
                <Mail size={20} className="mx-auto text-slate-700 mb-2" />
                No notifications.
              </td></tr>
            )}
            {rows.map((r) => {
              const meta = STATUS_META[r.status] || STATUS_META.pending;
              const Icon = meta.Icon;
              return (
                <tr key={r.id} className="hover:bg-slate-800/30 transition">
                  <td className="px-5 py-3 text-xs text-slate-400 whitespace-nowrap">
                    {formatDate(r.created_at, { includeTime: true })}
                  </td>
                  <td className="px-5 py-3">
                    <p className="text-slate-200 text-sm">{r.to_email}</p>
                    {r.firm_name && <p className="text-xs text-slate-500">{r.firm_name}</p>}
                  </td>
                  <td className="px-5 py-3 text-slate-300 text-sm">
                    {r.subject}
                    <p className="text-xs text-slate-500 mt-0.5">{r.kind} · {r.attempts} attempt{r.attempts === 1 ? '' : 's'}</p>
                    {r.last_error && <p className="text-xs text-red-400 mt-0.5 max-w-md truncate" title={r.last_error}>Error: {r.last_error}</p>}
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${meta.tone}`}>
                      <Icon size={11} /> {r.status}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    {r.status !== 'sent' && (
                      <button
                        onClick={() => retry(r)}
                        disabled={retrying === r.id}
                        className="px-2.5 py-1 rounded-lg text-xs bg-slate-800 hover:bg-indigo-900/40 hover:text-indigo-300 text-slate-300 disabled:opacity-40"
                      >
                        {retrying === r.id ? 'Sending…' : 'Send now'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-500">
        Email delivery uses <a href="https://resend.com" target="_blank" rel="noreferrer" className="underline hover:text-slate-300">Resend</a>.
        Set <code className="font-mono">RESEND_API_KEY</code> and (optionally) <code className="font-mono">NOTIFICATION_FROM</code> via{' '}
        <code className="font-mono">supabase secrets set</code> to enable sending.
      </p>
    </div>
  );
}
